// Friday report endpoints (13 §Endpoints `reports/friday`, §Friday report). Behind `kpi`.
import { z } from "zod";
import { ReportRecipient } from "../friday-report";
import { Email, WeekKey } from "../primitives";
import { FridayReportRecord } from "../rows";
import { CompanyItemParams, CompanyParams, PageQuery, page } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}/reports/friday";
const kpi = { feature: "kpi", tag: "reports" } as const;

export const FridayReportQuery = PageQuery.extend({ week: WeekKey.optional() });

export const FridayReportGenerate = z
  .strictObject({ week_key: WeekKey })
  .meta({ id: "FridayReportGenerate" });

/**
 * S1-17 review #3: whether `reports.send` would answer `report_stale` right now, computed by
 * the server with the same fingerprint comparison send uses (a draft, or a superseded or
 * drifted unfrozen version, is stale; a frozen version resends its own content and is not).
 * Advisory for the UI: send still re-checks inside its transaction.
 */
const reportStale = { report_stale: z.boolean() };

export const FridayReportListItem = FridayReportRecord.extend(reportStale).meta({
  id: "FridayReportListItem",
});

export const FridayReportDetail = FridayReportRecord.extend({
  ...reportStale,
  html_url: z.string().nullable(),
  pdf_url: z.string().nullable(),
}).meta({ id: "FridayReportDetail" });

export const FridayReportPatch = z
  .strictObject({ supervisor_note: z.string().max(10_000).nullable() })
  .meta({ id: "FridayReportPatch" });

export const FridayReportSend = z
  .strictObject({
    recipients: z.array(ReportRecipient).max(50).optional(),
    /** Send even if ledger or observations changed since generation (`report_stale`). */
    force: z.boolean().optional(),
  })
  .meta({ id: "FridayReportSend" });

/**
 * An agency operator settles a delivery whose outcome is unknown (the SMTP session broke after
 * submission, or the sender crashed after submitting), against the mail provider's records.
 *
 * Residual risk: exactly-once email delivery is impossible. Bytes a relay has accepted cannot be
 * recalled, and an SMTP session that breaks after the final dot leaves no way to know whether
 * the relay accepted the message. The ledger, fencing and submit deadline guarantee that no
 * second attempt overlaps the first; they cannot tell what the relay did. So this action never
 * guesses: the operator states the evidence (`provider_log`: the relay's delivery log;
 * `message_id_lookup`: a search for the delivery's deterministic Message-ID) and what it showed
 * (`note`), and both are recorded in the audit event.
 */
export const FridayReportReconcile = z
  .strictObject({
    email: Email,
    /** `send` (default) or the resend operation (`resend:{uuid}`) the delivery belongs to. */
    operation: z
      .string()
      .max(64)
      .regex(/^(send|resend:[0-9a-f-]{36})$/)
      .optional(),
    outcome: z.enum(["delivered", "not_delivered"]),
    /** Where the outcome was established; required (recorded in the audit event). */
    evidence: z.enum(["provider_log", "message_id_lookup"]),
    /** What the evidence showed (log line, query, reply), recorded in the audit event. */
    note: z.string().trim().min(10).max(2000),
  })
  .meta({ id: "FridayReportReconcile" });

/**
 * REPORTS-UI: the caller's own Friday report email in this company (13 §Friday report
 * "Sending": `subscribed ≠ false` per member). `eligible`: the caller holds owner, admin or
 * client viewer, the relations that receive the report by default; `subscribed`: not on the
 * opt-out list; `extra`: the caller's address is also one of the manager's extra recipients,
 * which reaches them whatever this says (explicit beats default, ./README "Recipients").
 */
export const FridayReportSubscription = z
  .object({ eligible: z.boolean(), subscribed: z.boolean(), extra: z.boolean() })
  .meta({ id: "FridayReportSubscription" });

export const FridayReportSubscriptionSet = z
  .strictObject({ subscribed: z.boolean() })
  .meta({ id: "FridayReportSubscriptionSet" });

const myBase = "/v1/companies/{co}/my/friday-report";

export const reportRoutes = {
  "reports.list": defineRoute({
    ...kpi,
    id: "reports.list",
    method: "get",
    path: base,
    summary: "List Friday reports (client viewers see sent reports only)",
    access: "seat_viewer",
    params: CompanyParams,
    query: FridayReportQuery,
    response: { status: 200, schema: page(FridayReportListItem) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "reports.generate": defineRoute({
    ...kpi,
    id: "reports.generate",
    method: "post",
    path: `${base}/generate`,
    summary: "Generate version N+1 for a week (also writes the week's publications)",
    access: "manager",
    params: CompanyParams,
    body: FridayReportGenerate,
    idempotency: "optional",
    response: { status: 201, schema: FridayReportRecord },
    errors: [],
    emits: ["friday_report.generated", "scorecard.published"],
  }),
  "reports.get": defineRoute({
    ...kpi,
    id: "reports.get",
    method: "get",
    path: `${base}/{id}`,
    summary: "Report snapshot and artefact URLs",
    access: "seat_viewer",
    params: CompanyItemParams,
    response: { status: 200, schema: FridayReportDetail },
    errors: [],
    emits: [],
  }),
  "reports.html": defineRoute({
    ...kpi,
    id: "reports.html",
    method: "get",
    path: `${base}/{id}/html`,
    summary: "Rendered HTML (the email body)",
    access: "seat_viewer",
    params: CompanyItemParams,
    response: { status: 200, contentType: "text/html" },
    errors: [],
    emits: [],
  }),
  "reports.pdf": defineRoute({
    ...kpi,
    id: "reports.pdf",
    method: "get",
    path: `${base}/{id}/pdf`,
    summary: "Rendered PDF",
    access: "seat_viewer",
    params: CompanyItemParams,
    response: { status: 200, contentType: "application/pdf" },
    errors: [],
    emits: [],
  }),
  "reports.update": defineRoute({
    ...kpi,
    id: "reports.update",
    method: "patch",
    path: `${base}/{id}`,
    summary: "Edit the supervisor note (a version whose send started gets version N+1)",
    access: "manager",
    params: CompanyItemParams,
    body: FridayReportPatch,
    response: { status: 200, schema: FridayReportRecord },
    errors: ["conflict"],
    emits: ["friday_report.updated"],
  }),
  "reports.send": defineRoute({
    ...kpi,
    id: "reports.send",
    method: "post",
    path: `${base}/{id}/send`,
    summary:
      "Send (idempotent per report id, version and recipient); 422 report_stale unless force. " +
      "force on a sent version with an Idempotency-Key is a resend (one per key)",
    access: "manager",
    params: CompanyItemParams,
    body: FridayReportSend,
    idempotency: "optional",
    response: { status: 200, schema: FridayReportRecord },
    errors: ["report_stale", "conflict"],
    emits: ["friday_report.sent", "friday_report.send_failed"],
  }),
  "reports.reconcile": defineRoute({
    ...kpi,
    id: "reports.reconcile",
    method: "post",
    path: `${base}/{id}/deliveries/reconcile`,
    summary:
      "Operator: settle an unconfirmed delivery from evidence (provider log or Message-ID " +
      "lookup), audited. Exactly-once email is impossible: accepted bytes cannot be recalled",
    access: "operator",
    params: CompanyItemParams,
    body: FridayReportReconcile,
    response: { status: 200, schema: FridayReportRecord },
    errors: ["conflict", "delivery_in_progress"],
    emits: ["friday_report.delivery_reconciled"],
  }),
  "reports.subscription": defineRoute({
    ...kpi,
    id: "reports.subscription",
    method: "get",
    path: myBase,
    summary:
      "The caller's own Friday report email: whether they receive it by default, opted out, " +
      "or are listed as an extra address (REPORTS-UI)",
    access: "reader",
    params: CompanyParams,
    response: { status: 200, schema: FridayReportSubscription },
    errors: [],
    emits: [],
  }),
  "reports.setSubscription": defineRoute({
    ...kpi,
    id: "reports.setSubscription",
    method: "put",
    path: myBase,
    summary:
      "Opt the caller (a person, signed in) out of the Friday report email, or back in; only " +
      "their own entry of the opt-out list changes (REPORTS-UI)",
    access: "reader",
    params: CompanyParams,
    body: FridayReportSubscriptionSet,
    response: { status: 200, schema: FridayReportSubscription },
    errors: [],
    emits: ["company.updated"],
  }),
} as const;
